import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';

// Skeleton Loader
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />
);

const AlertsPanel = ({ addToast }) => {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedAlert, setExpandedAlert] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchAlerts();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    intervalRef.current = setInterval(fetchAlerts, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await fetch(`${API_URL}/alerts`);
      const result = await response.json();
      setAlerts(result.alerts || []);
      setUnreadCount(result.unread_count || 0);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const markAsRead = async (alertId) => {
    try {
      await fetch(`${API_URL}/alerts/${alertId}/read`, { method: 'PUT' });
      await fetchAlerts();
    } catch (error) {
      addToast('Failed to mark as read', 'error');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await fetch(`${API_URL}/alerts/${alertId}/resolve`, { method: 'PUT' });
      addToast('Alert resolved', 'success');
      await fetchAlerts();
    } catch (error) {
      addToast('Failed to resolve', 'error');
    }
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          gradient: 'from-red-500/10 to-transparent',
          border: 'border-red-500/30',
          icon: (
            <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ),
          badge: 'bg-red-500/20 text-red-400 ring-red-500/30',
          label: 'Critical'
        };
      case 'high':
        return {
          gradient: 'from-[#FF6826]/10 to-transparent',
          border: 'border-[#FF6826]/30',
          icon: (
            <svg className="w-5 h-5 text-[#FF6826]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          badge: 'bg-[#FF6826]/20 text-[#FF6826] ring-[#FF6826]/30',
          label: 'High'
        };
      case 'medium':
        return {
          gradient: 'from-yellow-500/10 to-transparent',
          border: 'border-yellow-500/30',
          icon: (
            <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          badge: 'bg-yellow-500/20 text-yellow-400 ring-yellow-500/30',
          label: 'Medium'
        };
      default:
        return {
          gradient: 'from-[#6366f1]/10 to-transparent',
          border: 'border-[#6366f1]/30',
          icon: (
            <svg className="w-5 h-5 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          badge: 'bg-[#6366f1]/20 text-[#6366f1] ring-[#6366f1]/30',
          label: 'Info'
        };
    }
  };

  const getPriorityLabel = (alert) => {
    if (alert.severity === 'critical') return { label: 'P1', color: 'text-red-400 bg-red-500/10 border-red-500/20' };
    if (alert.severity === 'high') return { label: 'P2', color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' };
    if (alert.severity === 'medium') return { label: 'P3', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' };
    return { label: 'P4', color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20' };
  };

  const resolveAll = async (alertIds) => {
    await Promise.all(alertIds.map(id =>
      fetch(`${API_URL}/alerts/${id}/resolve`, { method: 'PUT' }).catch(() => {})
    ));
    addToast(`Resolved ${alertIds.length} alerts`, 'success');
    fetchAlerts();
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'unread') return !alert.is_read;
    if (filter === 'critical') return alert.severity === 'critical';
    return true;
  });

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-64" />
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            AI Alerts
            {unreadCount > 0 && (
              <span className="px-2.5 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                {unreadCount} new
              </span>
            )}
          </h1>
          <p className="text-zinc-500 text-sm mt-1 flex items-center gap-2">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse"></span>
              <span className="text-[#22c55e] text-xs font-medium">Live</span>
            </span>
            <span>·</span>
            {unreadCount > 0 ? `${unreadCount} unread alert${unreadCount > 1 ? 's' : ''} need your attention` : 'All caught up! No new alerts.'}
            <span>·</span>
            <span>Updated {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </p>
        </div>
        
        {/* Filter Tabs + Resolve All */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-[#18181B] p-1 rounded-xl border border-white/10">
            {[
              { id: 'all', label: 'All', count: alerts.length },
              { id: 'unread', label: 'Unread', count: alerts.filter(a => !a.is_read).length },
              { id: 'critical', label: 'Critical', count: alerts.filter(a => a.severity === 'critical').length }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center gap-2 ${
                  filter === f.id
                    ? 'bg-[#6366f1]/20 text-[#6366f1]'
                    : 'text-zinc-500 hover:text-white'
                }`}
              >
                <span>{f.label}</span>
                {f.count > 0 && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    filter === f.id ? 'bg-black/20' : 'bg-white/10'
                  }`}>
                    {f.count}
                  </span>
                )}
              </button>
            ))}
          </div>
          {alerts.filter(a => !a.is_resolved && !a.is_read).length > 0 && (
            <button
              onClick={() => resolveAll(alerts.filter(a => !a.is_resolved && !a.is_read).map(a => a.id))}
              className="px-3 py-2 bg-[#6366f1]/10 text-[#6366f1] border border-[#6366f1]/20 rounded-xl text-xs font-bold hover:bg-[#6366f1]/20 transition-colors flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Resolve All ({alerts.filter(a => !a.is_resolved && !a.is_read).length})
            </button>
          )}
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.map(alert => {
          const config = getSeverityConfig(alert.severity);
          const isExpanded = expandedAlert === alert.id;
          
          return (
            <div
              key={alert.id}
              className={`bg-gradient-to-r ${config.gradient} rounded-2xl border ${config.border} transition-colors ${
                !alert.is_read ? 'ring-1 ring-[#6366f1]/30' : ''
              } ${isExpanded ? 'ring-2 ring-[#6366f1]/20' : ''}`}
            >
              <div 
                className="p-5 cursor-pointer"
                onClick={() => setExpandedAlert(isExpanded ? null : alert.id)}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${config.badge.split(' ')[0]}`}>
                    {config.icon}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-white">{alert.title}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 ${config.badge}`}>
                            {config.label}
                          </span>
                          {(() => {
                            const p = getPriorityLabel(alert);
                            return (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${p.color}`}>
                                {p.label}
                              </span>
                            );
                          })()}
                          {!alert.is_read && (
                            <span className="w-2 h-2 bg-[#6366f1] rounded-full animate-pulse"></span>
                          )}
                        </div>
                        <p className="text-zinc-400 mt-1 text-sm line-clamp-2">{alert.message}</p>
                      </div>
                      
                      {/* Time & Actions */}
                      <div className="flex flex-col items-end gap-2">
                        <p className="text-xs text-zinc-600 font-mono whitespace-nowrap">
                          {formatTime(alert.created_at)}
                        </p>
                        <svg 
                          className={`w-5 h-5 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-5 pb-5 animate-fade-in">
                  {/* AI Analysis */}
                  {alert.ai_analysis && (
                    <div className="bg-black/30 rounded-xl p-4 border border-white/10 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <p className="text-xs font-bold text-[#6366f1] uppercase tracking-wider">AI Analysis</p>
                      </div>
                      <p className="text-sm text-zinc-300 leading-relaxed">{alert.ai_analysis}</p>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    {!alert.is_read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markAsRead(alert.id); }}
                        className="px-4 py-2 bg-white/5 text-zinc-400 rounded-xl text-sm font-medium hover:bg-white/10 hover:text-white border border-white/10 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Mark as Read
                      </button>
                    )}
                    {!alert.is_resolved && (
                      <button
                        onClick={(e) => { e.stopPropagation(); resolveAlert(alert.id); }}
                        className="px-4 py-2 bg-[#22c55e]/10 text-[#22c55e] rounded-xl text-sm font-medium hover:bg-[#22c55e]/20 border border-[#22c55e]/20 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Resolve
                      </button>
                    )}
                    {alert.is_resolved && (
                      <span className="px-4 py-2 bg-[#22c55e]/10 text-[#22c55e] rounded-xl text-sm font-medium border border-[#22c55e]/20 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Resolved
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Empty State */}
        {filteredAlerts.length === 0 && (
          <div className="text-center py-16 bg-[#09090B] rounded-2xl border border-white/10">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">All Clear!</h3>
            <p className="text-zinc-500">
              {filter === 'all' 
                ? "No alerts to show. Your campaigns are running smoothly."
                : filter === 'unread'
                  ? "You've read all your alerts. Great job staying on top of things!"
                  : "No critical alerts. Your accounts are in good health."
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsPanel;
